import { Location } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
  signal,
} from "@angular/core";
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatSort } from "@angular/material/sort";
import { DomSanitizer } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { FileItem, ParsedResponseHeaders } from "../../../../core/uploader/file-upload";
import { SessionStorageService } from "ngx-webstorage";
import { Observable, ReplaySubject } from "rxjs";
import { filter } from "rxjs/operators";
import { ROUTE_TRANSITION } from "../../../../app.animation";
import { ListColumn } from "../../../../core/list/list-column.model";
import { ListDatabase } from "../../../../core/list/list-database";
import { ListDataSource } from "../../../../core/list/list-datasource";
import { b64toBlob } from "../../../../core/utils/utils";
import * as fromRoot from "../../../../reducers";
import { AuditingComponent } from "../../../auditing/auditing.component";
import { AssetService } from "../../../services/asset.service";
import { UploaderService } from "../../../services/uploader.service";
import { AssetFormComponentBase } from "../../../shared/component-base/asset-form.component.base";
import { ComponentsDialogComponent } from "../../../shared/components-dialog/components-dialog.component";
import DialogUtils from "../../../shared/components-dialog/components-dialog.utils";
import { ServiceSummaryDTO } from "../../../shared/dto-models/serviceSummaryDTO.model";
import { AssetCommand } from "../../../shared/models/assetCommand";
import { AESEncryptDecryptService } from "../../../shared/services/aes-encrypt-decrypt.service";
import { FileScanResultCheckerService } from "../../../shared/services/file-scan-result-checker.service";
import { StorageService } from "../../../shared/services/storage.service";
import { requireMatch } from "../../../shared/validation/requireMatch";
import { ValidationProcessorService } from "../../../shared/validator/validation-processor.service";
import { ValidationModel } from "../../../shared/validator/validation.model";
import { getErrors } from "../../../shared/validator/validation.service";
import { DocumentDTO } from "./../../../shared/dto-models/documentDTO.model";
import { ServiceAddComponent } from "../../../shared/service-add/service-add.component";
import { ServiceAddService } from "./service-add/service-add.service";

@Component({
    templateUrl: "./asset-form.component.html",
    styleUrls: ["./asset-form.component.scss"],
    animations: [...ROUTE_TRANSITION],
    standalone: false
})
export class AssetFormComponent
  extends AssetFormComponentBase
  implements OnInit
{
  storeName = "global_buildingfilter";
  debug = false;

  desktopGap = "24px";
  mobileGap = "16px";

  desktopWidth = `0 0 calc(33.3333% - ${this.desktopGap}`;
  tabletWidth = `0 0 calc(33.3333% - ${this.desktopGap}`;
  smallTabletWidth = `0 0 calc(50% - ${this.mobileGap}`;
  mobileWidth = `0 0 calc(100% - ${this.mobileGap}`;

  selectedFile: File;
  removeDocument: boolean;
  base64Encoded: string;

  imagePath: any = "assets/img/placeholder.svg";
  isNewRecord = true;
  isUploading: boolean;
  servicesTabValid = signal(true);

  // services
  @ViewChild("tableServicePaginator", { static: true })
  tableServicePaginator: MatPaginator;
  @ViewChild("tableServiceSort", { static: true }) tableServiceSort: MatSort;
  columnsServices: ListColumn[] = [];
  pageSizeService = 10;
  resultsService: number;
  subjectServices$: ReplaySubject<ServiceSummaryDTO[]> = new ReplaySubject<
    ServiceSummaryDTO[]
  >(1);
  dataServices$: Observable<ServiceSummaryDTO[]>;
  dataSourceServices: ListDataSource<ServiceSummaryDTO> | null;
  databaseServices: ListDatabase<ServiceSummaryDTO>;
  validationRules: ValidationModel[] = [];

  @ViewChild("auditing", { static: true }) auditingComponent: AuditingComponent;

  constructor(
    store: Store<fromRoot.State>,
    formBuilder: UntypedFormBuilder,
    public assetService: AssetService,
    private router: Router,
    route: ActivatedRoute,
    public translate: TranslateService,
    private ref: ChangeDetectorRef,
    public snackbar: MatSnackBar,
    private dialog: MatDialog,
    private domSanitizer: DomSanitizer,
    private cdRef: ChangeDetectorRef,
    location: Location,
    private sessionStorageService: SessionStorageService,
    private localStorage: StorageService,
    private uploadService: UploaderService,
    private fileScanService: FileScanResultCheckerService,
    private aesService: AESEncryptDecryptService,
    private validationProcessorService: ValidationProcessorService,
    private serviceAddService: ServiceAddService
  ) {
    super(
      store,
      formBuilder,
      assetService,
      route,
      router,
      translate,
      snackbar,
      location,
      aesService,
    );

    this.defaultLoadMessage = translate.instant(
      "MESSAGES.SNACKBAR_LOADMESSAGE",
    );
    this.defaultErrorMessages = translate.instant(
      "MESSAGES.SNACKBAR_ERRORMESSAGES",
    );

    this.assetForm.addControl(
      "selectBuilding",
      new UntypedFormControl("", requireMatch),
    );
    this.assetForm.addControl("selectZone", new UntypedFormControl(""));
    this.assetForm.addControl("selectLocationType", new UntypedFormControl(""));
    this.assetForm.addControl(
      "selectLocation",
      new UntypedFormControl("", requireMatch),
    );

    this.assetForm.addControl(
      "assetComment",
      new UntypedFormControl("", new UntypedFormControl(""))
    );

    this.formResources["selectBuilding"] = {
      fieldName: translate.instant("ASSET.FORM.BUILDINGNAME"),
      errorMessage: "",
    };
    this.formResources["selectZone"] = {
      fieldName: translate.instant("ASSET.FORM.ZONENAME"),
      errorMessage: "",
    };
    this.formResources["selectLocationType"] = {
      fieldName: translate.instant("ASSET.FORM.LOCATIONTYPENAME"),
      errorMessage: "",
    };
    this.formResources["selectLocation"] = {
      fieldName: translate.instant("ASSET.FORM.LOCATIONNAME"),
      errorMessage: "",
    };
    this.formResources["services"] = {
      fieldName: translate.instant("ASSET.FORM.TABS.SERVICES"),
      errorMessage: "",
    };
    this.formResources["assetRef"] = {
      fieldName: translate.instant("ASSET.FORM.ASSETREF"),
      errorMessage: "",
    };

    this.formResources["assetComment"] = {
      fieldName: translate.instant("ASSET.FORM.COMMENTS"),
      errorMessage: "",
    };

    this.columnsServices = [
      {
        name: this.translate.instant("SERVICE.COLUMNS.CHECKBOX"),
        property: "checkbox",
        visible: false,
      },
      {
        name: this.translate.instant("SERVICE.COLUMNS.SERVICENAME"),
        property: "serviceName",
        visible: true,
        isModelProperty: true,
      },
      {
        name: this.translate.instant("SERVICE.COLUMNS.PARENTSERVICE"),
        property: "parentService",
        visible: true,
        isModelProperty: true,
      },
      {
        name: this.translate.instant("SERVICE.COLUMNS.CATEGORYNAME"),
        property: "categoryName",
        visible: true,
        isModelProperty: true,
      },
      {
        name: this.translate.instant("SERVICE.COLUMNS.STATUS"),
        property: "status",
        propertyId: "statusId",
        visible: true,
        isModelProperty: true,
      },
      {
        name: this.translate.instant("SERVICE.COLUMNS.ACTIONS"),
        property: "actions",
        visible: true,
      },
    ] as ListColumn[];

    this.validationMessages["nomatch"] = this.translate.instant(
      "MESSAGES.VALIDATION_MESSAGES.NOMATCH",
    );
  }

  async ngOnInit() {
    super.ngOnInit();

    this.debug = this.localStorage.retrieve("debug") === true;

    // set stored building
    if (!this.assetId) {
      const storedBuilding = this.sessionStorageService.retrieve(
        this.storeName,
      );
      if (storedBuilding) {
        this.asset.buildingId = storedBuilding.id;
        this.assetForm
          .get("selectBuilding")
          .setValue({ id: storedBuilding.id, name: storedBuilding.name });
      }
    }

    await this.applyValidationRules();
  }
  get assetTabFieldsValid() {
    const rules = this.validationRules.filter(
      (rule) => rule.clientTabId.toLowerCase() === "asset",
    );
    return this.validationProcessorService.rulesValid(this.assetForm, rules);
  }

  getDisplayName(value: any): string {
    return value && typeof value === "object" ? value.name : value;
  }

  async applyValidationRules() {
    this.validationRules = await this.validationProcessorService.process(
      this.assetForm,
      "Asset",
    );
    this.revalidateForm();
  }

  resetAdditionalProperties() {
    this.assetForm
      .get("selectBuilding")
      .setValue(
        { id: this.asset.buildingId, name: this.asset.buildingName },
        { emitEvent: false },
      );
    this.assetForm
      .get("selectZone")
      .setValue(
        { id: this.asset.zoneId, name: this.asset.zoneName },
        { emitEvent: false },
      );
    this.assetForm
      .get("selectLocationType")
      .setValue(
        { id: this.asset.locationTypeId, name: this.asset.locationTypeName },
        { emitEvent: false },
      );
    this.assetForm
      .get("selectLocation")
      .setValue(
        { id: this.asset.locationId, name: this.asset.locationName },
        { emitEvent: false },
      );
  }

  loadAdditionalData() {
    if (this.asset.documentId > 0) {
      this.preview(this.asset.documentId);
    }

    this.setServiceValidator();
    this.auditingComponent.refresh();
  }

  getAuditEventTypeId() {
    return `Asset:${this.assetId}`;
  }

  preview(documentId: number): any {
    this.assetService.previewImage(documentId).subscribe((r) => {
      this.previewFile(r);
    });
  }

  getErrors(name: string) {
    return getErrors(
      name,
      this.assetForm,
      this.formResources,
      this.validationMessages,
    );
  }

  // Services Tab
  get visibleColumnsServices() {
    return this.columnsServices
      .filter((column) => column.visible)
      .map((column) => column.property);
  }

  protected setServicesList(data: ServiceSummaryDTO[]) {
    this.subjectServices$.next(data);
    this.dataServices$ = this.subjectServices$.asObservable();

    this.databaseServices = new ListDatabase<ServiceSummaryDTO>();
    this.dataServices$.pipe(filter(Boolean)).subscribe((services: []) => {
      this.databaseServices.dataChange.next(services);
      this.resultsService = services.length;
    });
    this.dataSourceServices = new ListDataSource<ServiceSummaryDTO>(
      this.databaseServices,
      this.tableServiceSort,
      this.tableServicePaginator,
      this.columnsServices,
    );

    this.setServices();
  }

  onFilterChangeService(value) {
    if (!this.dataSourceServices) {
      return;
    }
    this.dataSourceServices.filter = value;
  }

  updateRowService(row: any) {
    const id = row ? btoa(this.aesService.encrypt(row.id)) : null;
    this.router.navigateByUrl(`service/form?id=${id}`);
  }

  removeRowService(row: any) {
    const index = this.asset.services.indexOf(row);
    this.asset.services.splice(index, 1);
    this.setServicesList(this.asset.services);
    this.assetForm.markAsDirty();
    this.setServiceValidator();
  }

  addServices() {
    this.dialog
      .open(ServiceAddComponent, {
        data: {
          gridService: this.serviceAddService,
          locationId: this.assetForm?.value?.selectLocation?.id,
          existingServices: this.asset.services,
        },
      })
      .afterClosed()
      .subscribe((data: ServiceSummaryDTO[]) => {
        if (!data) {
          return;
        }

        this.asset.services = data;
        this.setServicesList(this.asset.services);
        this.assetForm.markAsDirty();
        this.setServiceValidator();
      });
  }

  setServiceValidator() {
    this.assetForm.get("services").clearValidators();
    this.assetForm.get("services").updateValueAndValidity();
  }

  // image
  onFileSelected(event) {
    this.selectedFile = <File>event.target.files[0];
    event.target.value = "";

    if (this.selectedFile) {
      const scanner = this.uploadService.getFileUploader(
        "documentupload/scan",
        this.asset?.id,
      );
      this.isUploading = true;
      scanner.onErrorItem = (item, response, status, headers) =>
        this.onErrorImage(item, response, status, headers);
      scanner.onSuccessItem = (item, response, status, headers) =>
        this.onSuccessImage(item, response, status, headers);
      scanner.addToQueue([this.selectedFile]);
    }
  }

  onSuccessImage(
    item: FileItem,
    response: string,
    status: number,
    headers: ParsedResponseHeaders,
  ): any {
    const reader = new FileReader();
    reader.onload = this.handleReaderLoaded.bind(this);
    reader.readAsBinaryString(this.selectedFile);
    this.assetForm.markAsDirty();
    this.removeDocument = false;
    this.isUploading = false;
  }

  onErrorImage(
    item: FileItem,
    response: string,
    status: number,
    headers: ParsedResponseHeaders,
  ): any {
    const result = this.fileScanService.check(response);
    this.isUploading = false;
    if (!result) {
      this.setFilePlaceholder();
      return;
    }

    this.onFileRemoved();
    this.ref.detectChanges();
  }

  onFileRemoved() {
    const dialogMsg = this.translate.instant("MESSAGES.IMAGE_REMOVE");

    return new Promise((resolve, reject) => {
      this.dialog
        .open(ComponentsDialogComponent, {
          width: "400px",
          data: {
            user_input: false,
            title: this.translate.instant("MESSAGES.IMAGE_REMOVAL"),
            label: dialogMsg,
            required: false,
            okButton: this.translate.instant("COMMON.YES"),
            cancelButton: this.translate.instant("COMMON.NO"),
          },
        })
        .afterClosed()
        .subscribe(async (data: any) => {
          if (data && data.ok) {
            this.setFilePlaceholder();
          }
          resolve(null);
        });
    });
  }

  setFilePlaceholder() {
    this.imagePath = "assets/img/placeholder.svg";
    this.selectedFile = null;
    this.removeDocument = true;
    this.assetForm.markAsDirty();
    this.ref.detectChanges();
  }

  previewFile(content: any) {
    const blob = b64toBlob(content, this.asset.document.contentType);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    document.body.appendChild(a);
    a.setAttribute("style", "display: none");
    this.imagePath = this.domSanitizer.bypassSecurityTrustUrl(url);
  }

  handleReaderLoaded(readerEvt) {
    const binaryData = readerEvt.target.result;
    this.base64Encoded = btoa(binaryData);

    this.imagePath = "data:image/png;base64," + this.base64Encoded;
    this.cdRef.detectChanges();
  }

  onLocationSelect(event: any) {
    if (event) {
      this.asset.services = [];
      this.setServicesList(this.asset.services);
      this.setServiceValidator();
    }
  }

  setServices() {
    const services: UntypedFormArray = new UntypedFormArray(this.asset.services.map(item => new UntypedFormControl(item)));
    this.assetForm.setControl('services', services);
    this.servicesTabValid.set(this.asset?.services.length > 0);
  }

  // Save
  prepareSaveAsset(): AssetCommand {
    const formModel = this.assetForm.value;
    this.revalidateForm();
    if (this.assetForm.invalid) {
      return null;
    }
    let servicesDeepCopy: ServiceSummaryDTO[] = [];
    if (this.asset.services) {
      servicesDeepCopy = this.asset.services.map((service: ServiceSummaryDTO) =>
        Object.assign({}, service),
      );
    }

    if (servicesDeepCopy.length === 0) {
      this.snackBarHandler(
        this.translate.instant("ASSET.ERRORS.SERVICE_REQUIRED"),
      );
      this.servicesTabValid.set(false);
      return null;
    } else {
      this.servicesTabValid.set(true);
    }

    let saveAsset: AssetCommand;
    let document: DocumentDTO = null;

    if (this.selectedFile != null) {
      document = {
        dtoState: "",
        blobName: "",
        containerName: "",
        contentType: this.selectedFile.type,
        fileName: this.selectedFile.name,
        fileSizeInBytes: this.selectedFile.size,
        fileContents: this.base64Encoded,
        documentId: this.asset.documentId != null ? this.asset.documentId : 0,
        directoryName: "",
        documentTypeName: "",
        fileUrl: "",
        createdBy: "",
        createdDate: "",
        updatedBy: "",
        updatedDate: "",
        rowVersion: "",
        category: "",
        categoryId: 0,
        description: "",
        descriptionDisplayValue: "",
        descriptionTranslations: "",
        status: "",
        statusId: 0,
        actions: [],
      };
    }

    saveAsset = {
      id: this.asset.id,
      name: formModel.name,
      assetRef: formModel.assetRef,
      assetComment: formModel.assetComment,
      locationId: formModel.selectLocation.id,
      rowVersion: this.asset.rowVersion,
      services: servicesDeepCopy != null ? servicesDeepCopy : null,
      documentId: this.asset.documentId,
      removeDocument: this.removeDocument,
      document: document,
    };

    return saveAsset;
  }

  async changeStatusOverride(id: number, newStatus: number) {
    const DEACTIVATE = 4;
    if (newStatus === DEACTIVATE) {
      const hasChildren = await DialogUtils.showHasChildrenConfirmation(
        this.dialog,
        this.translate,
        this.assetService,
        id,
      );
      if (hasChildren) {
        return;
      }
    }
    this.changeStatus(id, newStatus);
  }
}
